import { GOAL_IDS, triggerCroConversionGoal, withCroVariant } from '@hoc/withCroVariant';
import { ReactComponent as Phone } from '@img/icons/phone.svg';
import { CONTACT_US_FORM_MODAL_OPEN_EVENT } from '@stories/Widgets/Forms/ContactUsFormModal/ContactUsFormModal';
import React, { useEffect, useRef, useState } from 'react';
import { ContactCtaStyles as S } from './ContactCta.styles';
const openContactForm = () => {
    triggerCroConversionGoal(GOAL_IDS.ContactCta_Click);
    window.dispatchEvent(new Event(CONTACT_US_FORM_MODAL_OPEN_EVENT));
};
/** Number of lines an element's text wraps onto */
const lineCount = (element) => {
    const range = document.createRange();
    range.selectNodeContents(element);
    return new Set(Array.from(range.getClientRects()).map((rect) => Math.round(rect.top))).size;
};
/**
 * The header hides its telephone number below 1600px as the bar runs out of room. Show it in full, but fall back
 * to the icon when it would push the navigation items onto two lines, and then to tighter navigation spacing
 * (data-contact-cta-compact on the bottom bar) when even that doesn't fit.
 */
const useBarFit = (barRef) => {
    const [fit, setFit] = useState('full');
    useEffect(() => {
        const bottomInner = barRef.current?.closest('[data-header-bottom-inner]');
        if (!bottomInner) {
            return;
        }
        let timer;
        let frame;
        const navWraps = () => Array.from(bottomInner.querySelectorAll('nav ul > li > a')).some((link) => lineCount(link) > 1);
        const apply = (next) => {
            bottomInner.dataset.contactCtaCompact = String(next === 'compact');
            setFit(next);
        };
        // Measure each step after it has rendered, and stop at the first that keeps the navigation on one line
        const tryFit = (steps) => {
            apply(steps[0]);
            if (steps.length > 1) {
                frame = window.requestAnimationFrame(() => {
                    if (navWraps()) {
                        tryFit(steps.slice(1));
                    }
                });
            }
        };
        const update = () => {
            window.cancelAnimationFrame(frame);
            tryFit(['full', 'icon', 'compact']);
        };
        const handleResize = () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(update, 150);
        };
        update();
        // Watch the bar itself rather than the window: a scrollbar appearing as the page loads narrows it without a
        // resize event (Windows browsers with classic scrollbars)
        const Observer = window.ResizeObserver;
        let lastWidth = bottomInner.clientWidth;
        const observer = Observer
            ? new Observer(() => {
                if (bottomInner.clientWidth !== lastWidth) {
                    lastWidth = bottomInner.clientWidth;
                    handleResize();
                }
            })
            : undefined;
        if (observer) {
            observer.observe(bottomInner);
        }
        else {
            window.addEventListener('resize', handleResize);
        }
        // Web fonts change text widths once they load (document.fonts isn't in this TypeScript version's DOM types)
        document.fonts?.ready.then(update);
        return () => {
            window.clearTimeout(timer);
            window.cancelAnimationFrame(frame);
            observer?.disconnect();
            window.removeEventListener('resize', handleResize);
            delete bottomInner.dataset.contactCtaCompact;
        };
    }, [barRef]);
    return fit;
};
const ContactCtaBar = ({ text, telephoneNumber }) => {
    const barRef = useRef(null);
    const telephoneFits = useBarFit(barRef) === 'full';
    return (React.createElement(S.Bar, { ref: barRef, "data-contact-cta": "bar", "data-icon-only": !telephoneNumber || !telephoneFits },
        telephoneNumber && (React.createElement(S.Telephone, { href: `tel:${telephoneNumber}`, "aria-label": "Telephone Number", "data-icon-only": !telephoneFits },
            React.createElement(Phone, null),
            React.createElement("span", null, telephoneNumber))),
        React.createElement(S.BarButton, { id: "contact-cta-bar", type: "button", "data-compact": !!telephoneNumber && telephoneFits, "aria-haspopup": "dialog", "aria-controls": "contact-us-form-modal", onClick: openContactForm }, text)));
};
const ContactCta = (props) => {
    if (props.placement === 'bar') {
        return React.createElement(ContactCtaBar, { ...props });
    }
    return (React.createElement(S.NavItem, { "data-contact-cta": "nav" },
        React.createElement(S.NavButton, { id: "contact-cta-nav", type: "button", "aria-haspopup": "dialog", "aria-controls": "contact-us-form-modal", onClick: openContactForm }, props.text)));
};
export default withCroVariant(ContactCta);
